import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { BehaviorSubject, take } from "rxjs";
import { Platform } from '@ionic/angular';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications, Token, PushNotificationSchema } from '@capacitor/push-notifications';

import { GroovePlanApiService } from "../grooveplan-api.service";
import { StorageService } from "../internals/storage.service";
import { PreferencesService, FCM_TOKEN } from "../internals/preferences.service";
import { SecureStorageService } from '../internals/secure-storage.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends GroovePlanApiService {

  private token = new BehaviorSubject<string | null>(null);
  public token$ = this.token.asObservable();

  public redirectLink = new BehaviorSubject<string | null>(null);
  public redirectLink$ = this.redirectLink.asObservable();

  constructor(
    private preferencesService: PreferencesService,
    private platform: Platform,
    _httpClient: HttpClient,
    storageService: StorageService,
    router: Router,
    secureStorage: SecureStorageService
  ) {
    super(_httpClient, storageService, secureStorage, router);
    this.entityUrl = 'notification';
  }

  /**
   * Initialisation globale, appelée AU BON MOMENT
   * après login ou dans app.component une fois platform.ready() OK
   */
  async initialiserNotifications() {
    await this.platform.ready();

    if (Capacitor.getPlatform() === 'web') { // Push non dispo sur le web
      return;
    }

    await this.addListeners();
    await this.registerPush();
  }

  /**
   * Ajoute les listeners d’événements
   */
  private async addListeners() {
    console.log('addListeners')
    // 1. Token FCM reçu
    PushNotifications.addListener('registration', async (token: Token) => {
      const newToken = token.value;
      if (!newToken) return;

      const saved = await this.preferencesService.get(FCM_TOKEN, null);

      if (saved === newToken) {
        console.log('Token déjà enregistré → skip 2');
        this.envoyerTokenAuServeur(newToken);
        return;
      }

      await this.preferencesService.set(FCM_TOKEN, JSON.stringify(newToken));
      this.envoyerTokenAuServeur(newToken);
    });

    // 2. Erreur d’inscription
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Erreur registration push', err);
    });

    // 3. Notification reçue
    PushNotifications.addListener('pushNotificationReceived', (notif: PushNotificationSchema) => {
      console.log('Notification reçue', notif);
    });

    // 4. Action sur notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Notification clicked:', notification);
      const action = notification.notification.data?.action;
      const link = notification.notification.data?.link;

      if(action === 'open_app' && link) {
        this.redirectLink.next(link);
      }
    });
  }

  /**
   * Demande permissions + enregistre pour recevoir un token FCM
   */
  private async registerPush() {
    console.log('registerPush');
    console.log(Capacitor.isNativePlatform()); 
    console.log(Capacitor.getPlatform());
    const info = await Device.getInfo();

    // if(+info.osVersion >= 13) {
      let perm = await PushNotifications.checkPermissions();

      if (perm.receive === 'prompt') {
        perm = await PushNotifications.requestPermissions();
      }
      if (perm.receive !== 'granted') {
        throw new Error('Permission push refusée');
      }
    // }

    await PushNotifications.register();
  }

  /**
   * Envoie le token de notification push au serveur
   */
  async envoyerTokenAuServeur(token: string): Promise<void> {

    /**
     * ⏳ Attente de l’initialisation de la plateforme Ionic
     * (obligatoire avant d’utiliser Device / Capacitor)
     */
    await this.platform.ready();

    /**
     * 📱 Récupération de l’identifiant du device
     * → uniquement en environnement natif
     */
    let deviceId: string | null = null;

    if (Capacitor.getPlatform() !== 'web') {
      const info = await Device.getId();
      deviceId = info.identifier;
    }

    /**
     * 🔐 Récupération des headers d’authentification
     * (asynchrone car token stocké en Secure Storage)
     */
    const headers = await this.getAuthHeaders();

    /**
     * 🚀 Envoi du token au backend
     */
    this.post(
      `${this.entityUrl}/subscribe`,
      {
        token,
        deviceId,
        platform: Capacitor.getPlatform()
      },
      headers
    ).pipe(take(1))
    .subscribe({
      /**
       * ✅ Token correctement enregistré côté serveur
       */
      next: (res: any) => {
        console.log('Token envoyé', res);
        this.token.next(token);
      },

      /**
       * ⚠️ Gestion des erreurs
       * - code 303 : token déjà existant / déjà enregistré
       */
      error: (err) => {
        console.error('Erreur envoi token', err);

        if (err.status === 303) {
          // On considère quand même le token comme valide localement
          this.token.next(token);
        }
      }
    });
  }


  sendTo(userId: any) {
    return this.post('notification/sendTo', {
      user_id: userId
    });
  }
}
